'use client';

import { useController } from 'react-hook-form';
import type { DeepMap, FieldError, FieldValues, UseControllerProps } from 'react-hook-form';
import formStyle from '../../../styles/modules/form.module.scss';
import type { VariantType } from '../../../types';
import InputCheckbox from '../../input/InputCheckbox/InputCheckbox';
import FormLabel from '../FormLabel/FormLabel';
import styles from './FormCheckbox.module.scss';
import raffleStyles from './FormCheckbox.raffle.module.scss';

type Props<T extends FieldValues> = {
  label?: string;
  items: { value: string; child: React.ReactNode }[];
  required?: boolean;
  onChange?: () => void;
  hint?: string;
  variant?: VariantType;
  spacing?: 'narrow' | 'normal' | 'wide';
} & UseControllerProps<T>;

function FormCheckbox<T extends FieldValues>({
  name,
  items,
  label,
  control,
  onChange,
  hint,
  required,
  variant = 'default',
  spacing,
}: Props<T>) {
  const {
    field,
    fieldState: { error },
  } = useController<T>({ name, control, defaultValue: [] as T[typeof name] });

  // --color-primaryの指定があるので分ける
  const currentStyles = variant === 'raffle' ? raffleStyles : styles;

  return (
    <div className={formStyle.formComponentStyle}>
      {/* ラベル */}
      {label && <FormLabel showRequiredMark={required}>{label}</FormLabel>}
      {/* フィールドブロック */}
      <div className={error ? formStyle.hasError : ''}>
        <div
          className={`${currentStyles.checkboxes} ${required && spacing === 'wide' ? currentStyles['required-wide'] : ''}`}
        >
          {items.map((item) => (
            <InputCheckbox
              key={item.value}
              name={field.name}
              inputRef={field.ref}
              checked={field.value.includes(item.value)}
              onChange={(e: React.ChangeEvent) => {
                if ((e.target as HTMLInputElement).checked) {
                  field.onChange(field.value.concat([item.value]));
                } else {
                  field.onChange(field.value.filter((fv: string) => fv !== item.value));
                }
                if (onChange) {
                  onChange();
                }
              }}
              inputValue={item.value}
              spacing={spacing}
            >
              {item.child}
            </InputCheckbox>
          ))}
          {/* labelがない場合の必須マーク */}
          {!label && required && <span className={currentStyles.requiredMarkOnly}>*</span>}
        </div>
        {hint && <p className={formStyle.hint}>{hint}</p>}
        {/* エラー */}
        {error && <p className={formStyle.validateErrors}>{(error as DeepMap<FieldValues, FieldError>).message}</p>}
      </div>
    </div>
  );
}

export default FormCheckbox;
